import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Search, Book, Building2, Star, Wallet, Gift, Users, Shield, 
  MapPin, Globe, HelpCircle, Phone, Mail, ChevronRight, ChevronDown,
  Sparkles, Award, Target, CreditCard, ExternalLink
} from 'lucide-react';
import Navbar from '@/components/Navbar';

const SUPPORT_PHONE = "(216) 626-7560";
const SUPPORT_EMAIL = "support@blackified.com";

const categories = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: Book,
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/10',
    articles: [
      { id: 'what-is-blackified', title: 'What is Blackified?' },
      { id: 'create-account', title: 'Creating Your Account' },
      { id: 'finding-businesses', title: 'Finding Black-Owned Businesses' },
      { id: 'using-map', title: 'Using the Map View' },
      { id: 'personalized-recommendations', title: 'Personalized Recommendations' },
    ]
  },
  {
    id: 'business-owners',
    title: 'For Business Owners',
    icon: Building2,
    color: 'text-green-400',
    bgColor: 'bg-green-500/10',
    articles: [
      { id: 'list-business', title: 'Listing Your Business' },
      { id: 'online-business', title: 'Online-Only Businesses' },
      { id: 'verification-process', title: 'Verification Process' },
      { id: 'claim-business', title: 'Claiming an Existing Listing' },
      { id: 'multiple-locations', title: 'Managing Multiple Locations' },
      { id: 'business-analytics', title: 'Business Analytics Dashboard' },
      { id: 'marketplace-selling', title: 'Selling Products & Services' },
    ]
  },
  {
    id: 'melanin-rewards',
    title: 'Melanin (MEL) Rewards',
    icon: Sparkles,
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/10',
    articles: [
      { id: 'what-is-mel', title: 'What is Melanin (MEL)?' },
      { id: 'earning-mel', title: 'How to Earn MEL' },
      { id: 'spending-mel', title: 'Spending Your MEL' },
      { id: 'buy-mel', title: 'Purchasing MEL' },
      { id: 'melanin-account', title: 'Melanin Account (Virtual USD)' },
    ]
  },
  {
    id: 'community',
    title: 'Community Features',
    icon: Users,
    color: 'text-orange-400',
    bgColor: 'bg-orange-500/10',
    articles: [
      { id: 'reviews-ratings', title: 'Reviews & Ratings' },
      { id: 'bounty-system', title: 'Bounty System (Gig Board)' },
      { id: 'referral-program', title: 'Referral Program' },
      { id: 'community-verification', title: 'Community Verification' },
      { id: 'delivery-driver', title: 'Become a Delivery Driver' },
    ]
  },
  {
    id: 'verification',
    title: 'Verification & Trust',
    icon: Shield,
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-500/10',
    articles: [
      { id: 'verification-badges', title: 'Understanding Verification Badges' },
      { id: 'social-proof', title: 'Social Proof Verification' },
      { id: 'domain-verification', title: 'Domain Ownership Verification' },
      { id: 'melanin-score', title: 'Melanin Score Explained' },
    ]
  },
];

const articleContent = {
  'what-is-blackified': {
    title: 'What is Blackified?',
    content: `
Blackified is the premier directory for discovering and supporting Black-owned businesses. Our platform connects consumers with verified Black-owned businesses across all categories, from restaurants and retail to professional services and online brands.

**Our Mission**
To empower Black entrepreneurs by increasing visibility, driving customer traffic, and building a supportive community around Black-owned businesses.

**Key Features**
- **Comprehensive Directory**: Find businesses by category, location, or search
- **Verification System**: Trust badges ensure businesses are genuinely Black-owned
- **MEL Rewards**: Earn and spend Melanin points for engaging with the platform
- **Map View**: Discover businesses near you with our interactive map
- **Reviews & Ratings**: Share your experiences and help others discover great businesses
- **Bounty System**: Help grow the directory and earn rewards
    `
  },
  'create-account': {
    title: 'Creating Your Account',
    content: `
Getting started with Blackified is quick and easy!

**Sign Up Options**
1. **Google Sign-In**: Click "Sign In" and select "Continue with Google" for instant access
2. **Email Registration**: Coming soon

**After Signing Up**
- Complete your profile (optional)
- Start exploring Black-owned businesses
- Save favorites for quick access
- Earn MEL rewards for various activities

**Your Dashboard**
Once logged in, access your dashboard to:
- View and manage your submitted businesses
- Check your MEL wallet balance
- See your saved favorites
- Track your referrals
    `
  },
  'finding-businesses': {
    title: 'Finding Black-Owned Businesses',
    content: `
Blackified offers multiple ways to discover Black-owned businesses:

**Search Bar**
- Enter keywords, business names, or categories
- Results show verified businesses matching your query

**Category Filters**
- Browse by category: Restaurants, Retail, Services, Beauty & Wellness, Professional Services, and more

**Location Filters**
- **Local Mode**: Shows businesses near your location
- **All Mode**: Shows both local and online businesses  
- **Online Mode**: Shows only online businesses (ignores location)

**Sorting Options**
- Default (relevance)
- Community Top Picks (by Melanin Score)
- Rating
- Newest

**Distance Filter**
In Local mode, use the distance slider to find businesses within your preferred radius (1-50 miles).
    `
  },
  'using-map': {
    title: 'Using the Map View',
    content: `
Our interactive map helps you visualize businesses in your area.

**Accessing the Map**
- Click the "Map" button on the Directory page
- Map is available in "Local" and "All" modes
- Disabled in "Online" mode (online businesses don't have physical locations)

**Map Features**
- **Pins**: Each verified business appears as a pin on the map
- **Clusters**: When zoomed out, nearby businesses are grouped
- **Click Pins**: Tap a pin to see business details
- **Your Location**: Blue dot shows your current position

**Distance Slider**
Adjust the radius to show businesses within your preferred distance. The map updates in real-time.
    `
  },
  'list-business': {
    title: 'Listing Your Business',
    content: `
Add your Black-owned business to our directory and reach thousands of customers!

**How to List**
1. Sign in to your account
2. Click "List Your Business" in the navigation
3. Fill out the business information form
4. Submit for verification

**Required Information**
- Business Name
- Category
- Description
- Contact Information (Phone, Email, Website)
- Location (for physical businesses)
- Service Area (for online businesses)

**After Submission**
- Your listing enters "Pending" status
- Our team reviews submissions within 24-48 hours
- You'll earn 0.50 MEL once verified
- Verified businesses appear in search results

**Tips for Approval**
- Provide accurate, complete information
- Include a working website or social media
- Ensure your business is genuinely Black-owned
    `
  },
  'online-business': {
    title: 'Online-Only Businesses',
    content: `
Blackified supports businesses without physical storefronts!

**What Qualifies as Online-Only?**
- E-commerce stores
- Digital services
- Freelancers and consultants
- SaaS products
- Content creators

**Listing an Online Business**
1. When submitting, toggle "This is an Online Business"
2. Address fields become optional
3. Select your Service Area (Global, USA, or specific states)

**Online Business Verification**
Online businesses have additional verification options:
- **Social Proof**: Link your Instagram/TikTok for AI analysis
- **Domain Verification**: Prove website ownership with a meta tag

**Appearing in Search**
- Use the "Online" filter to find online-only businesses
- Online businesses are sorted by Melanin Score by default
- Featured online brands appear in the carousel
    `
  },
  'verification-process': {
    title: 'Verification Process',
    content: `
Verification builds trust and increases visibility for your business.

**Verification Levels**

1. **Pending** (Yellow Badge)
   - Initial status after submission
   - Business is under review
   - Not visible in public search

2. **Verified** (Green Badge)
   - Approved by our team
   - Appears in all search results
   - Eligible for boosts and promotions

3. **Rejected** (Red Badge)
   - Did not meet criteria
   - Reason provided in dashboard
   - Can resubmit with corrections

**Additional Verification (Online Businesses)**
- Social Proof Score (0-100%)
- Domain Verified Badge
- Higher scores = better search ranking

**Timeline**
Most submissions are reviewed within 24-48 hours.
    `
  },
  'claim-business': {
    title: 'Claiming an Existing Listing',
    content: `
See your business already listed? You can claim ownership!

**How to Claim**
1. Find your business in the directory
2. Click "Claim This Business" button
3. Submit verification documents
4. Wait for admin approval

**Required Proof**
- Business license or registration
- Government ID matching business owner
- Utility bill or bank statement with business name

**After Claiming**
- Full editing access to the listing
- Access to analytics dashboard
- Ability to respond to reviews
- Manage multiple locations
    `
  },
  'multiple-locations': {
    title: 'Managing Multiple Locations',
    content: `
Businesses with multiple locations can manage them all from one dashboard.

**Adding Locations**
1. Go to Dashboard > Locations tab
2. Select your verified business
3. Click "Add Location"
4. Enter the new address details

**Each Location Has**
- Unique address and coordinates
- Individual business hours
- Location-specific phone number
- Separate reviews

**On the Business Page**
Visitors see a location selector dropdown to choose which location to view.
    `
  },
  'business-analytics': {
    title: 'Business Analytics Dashboard',
    content: `
Track your business performance with our analytics dashboard.

**Available Metrics**
- **Profile Views**: How many people viewed your listing
- **Click-Through Rate**: Website/phone clicks
- **Search Appearances**: How often you appear in search
- **Review Analytics**: Rating trends over time

**Accessing Analytics**
1. Go to your Dashboard
2. Click the "Analytics" tab
3. Select a verified business

**Boost Performance**
- Higher Melanin Score = better ranking
- Respond to reviews to boost engagement
- Keep information up-to-date
    `
  },
  'what-is-mel': {
    title: 'What is Melanin (MEL)?',
    content: `
Melanin (MEL) is Blackified's internal rewards currency.

**Value Proposition**
- Earn MEL for contributing to the platform
- Spend MEL to promote your business
- Convert MEL to cryptocurrency (1:1 with MELANIN token)

**MEL Balance**
Check your balance in the Wallet section of your dashboard. You'll see:
- Available Balance
- Pending Balance (from recent activities)
- Transaction History

**Platform Fee**
A 10% platform fee applies to all MEL transactions to support platform development.
    `
  },
  'earning-mel': {
    title: 'How to Earn MEL',
    content: `
Multiple ways to earn Melanin rewards!

**Earning Activities**

| Activity | Reward |
|----------|--------|
| Business Verified | +0.50 MEL |
| Write a Review | +0.10 MEL |
| Domain Verified | +2.00 MEL |
| Referral Signs Up | +1.00 MEL |
| Claim Bounty | Varies |

**Bounty System**
Complete bounties on the Gig Board to earn larger MEL rewards. Bounties include finding businesses in specific categories or locations.

**Referral Program**
Share your unique referral link. When someone signs up and submits a business, you both earn MEL!
    `
  },
  'spending-mel': {
    title: 'Spending Your MEL',
    content: `
Use your MEL to boost your business visibility!

**Boost Options**

1. **Featured Listing** (5 MEL)
   - Appear in "Featured" section on homepage
   - Duration: 7 days

2. **Premium Badge** (10 MEL)
   - Gold badge on your listing
   - Priority in search results
   - Duration: 30 days

3. **Community Boost** (Any amount)
   - Anyone can boost any business
   - Increases Melanin Score
   - Better search ranking

**How to Boost**
1. Go to your Dashboard
2. Click "Promote" on a verified business
3. Select boost type
4. Confirm MEL deduction
    `
  },
  'buy-mel': {
    title: 'Purchasing MEL',
    content: `
Need more MEL? Purchase packages with your credit card.

**MEL Packages**
- 10 MEL - $5
- 25 MEL - $10
- 50 MEL - $18
- 100 MEL - $30

**How to Purchase**
1. Go to Dashboard > Wallet
2. Click "Buy MEL" tab
3. Select a package
4. Complete payment via Stripe

**Payment Security**
All payments are processed securely through Stripe. We never store your card details.
    `
  },
  'withdraw-crypto': {
    title: 'Crypto Withdrawals',
    content: `
Convert your MEL to MELANIN tokens on Ethereum!

**Requirements**
- Minimum withdrawal: 10 MEL
- Connected Ethereum wallet (via RainbowKit)

**How to Withdraw**
1. Go to Dashboard > Wallet
2. Click "Withdraw" tab
3. Connect your wallet (MetaMask, Rainbow, etc.)
4. Enter withdrawal amount
5. Confirm transaction

**Processing Time**
Withdrawals are processed within 24-48 hours. You'll receive MELANIN tokens at a 1:1 ratio (minus 10% platform fee).

**Supported Wallets**
- MetaMask
- Rainbow
- Coinbase Wallet
- WalletConnect
    `
  },
  'reviews-ratings': {
    title: 'Reviews & Ratings',
    content: `
Help the community by sharing your experiences!

**Writing a Review**
1. Visit any verified business page
2. Scroll to "Reviews" section
3. Select your star rating (1-5)
4. Write your review
5. Submit and earn 0.10 MEL!

**Review Guidelines**
- Be honest and constructive
- Focus on your actual experience
- Avoid inappropriate language
- Include helpful details

**Business Responses**
Business owners can respond to reviews from their dashboard.
    `
  },
  'bounty-system': {
    title: 'Bounty System (Gig Board)',
    content: `
The Gig Board lets users create and claim bounties for finding businesses.

**What are Bounties?**
Bounties are rewards (in MEL) offered for finding specific types of businesses. Example: "Find a Black-owned bakery in Atlanta - 5 MEL reward"

**Creating a Bounty**
1. Go to the Bounty/Gig Board page
2. Click "Create Bounty"
3. Describe what you're looking for
4. Set the MEL reward (escrowed from your wallet)
5. Wait for someone to claim it

**Claiming a Bounty**
1. Browse available bounties
2. Find a matching business
3. Submit the business listing
4. If approved, you receive the reward!

**Featured Bounties**
High-value bounties are featured at the top of the Gig Board.
    `
  },
  'referral-program': {
    title: 'Referral Program',
    content: `
Grow the community and earn rewards!

**Your Referral Link**
Find your unique referral link in the Wallet section of your dashboard.

**How It Works**
1. Share your link with friends
2. They sign up using your link
3. When they submit a verified business:
   - You earn 1.00 MEL
   - They get bonus MEL too!

**Tracking Referrals**
View your referral stats in the Wallet tab.
    `
  },
  'community-verification': {
    title: 'Community Verification',
    content: `
The community helps verify Black-owned status through voting.

**How It Works**
- Users can vote on whether a business is Black-owned
- High community confidence contributes to verification
- Combined with admin review for final decision

**Voting**
- Visit a business page
- Click "Verify Black-Owned" button
- Your vote is recorded anonymously

**Community Score**
Businesses with high community votes receive a "Community Verified" indicator.
    `
  },
  'verification-badges': {
    title: 'Understanding Verification Badges',
    content: `
Badges help users identify trusted businesses.

**Badge Types**

🟢 **Verified** (Green)
- Admin-approved Black-owned business
- Passed our verification process

🟣 **Premium** (Purple/Gold)
- Paid premium badge
- Extra visibility in search

🔵 **Domain Verified** (Blue Shield)
- Proved website ownership
- Extra trust for online businesses

📊 **Confidence Score**
- AI-analyzed social proof
- Higher percentage = more confidence

⭐ **Top Pick**
- High Melanin Score
- Community favorite
    `
  },
  'social-proof': {
    title: 'Social Proof Verification',
    content: `
Verify your Black-owned status through social media!

**How It Works**
1. Go to Dashboard > My Businesses
2. Click "Verify Social Proof" on your online business
3. Enter your Instagram/TikTok handles
4. Optionally paste your bio text
5. Our AI analyzes for Black-owned indicators

**What We Look For**
- Keywords: "Black Owned", "BOP", "Melanin", "Buy Black"
- Cultural references and emojis
- Community hashtags

**Confidence Score**
- 70%+ = High Confidence (Green)
- 40-69% = Medium Confidence (Yellow)
- Below 40% = Low Confidence (Red)

**Tips for Higher Score**
- Include "Black Owned" in your social bio
- Use relevant hashtags
- Paste your actual bio text for better accuracy
    `
  },
  'domain-verification': {
    title: 'Domain Ownership Verification',
    content: `
Prove you own your business website and earn 2 MEL!

**3-Step Process**

**Step 1: Generate Token**
- Click "Claim This Site" on your business
- We generate a unique verification code

**Step 2: Add Meta Tag**
- Copy the provided meta tag
- Add it to your website's <head> section
- Example: \`<meta name="blackified-verification" content="your-token">\`

**Step 3: Verify**
- Click "Verify Domain"
- We check your website for the tag
- Success = Domain Verified badge + 2 MEL!

**Website Builder Tips**
- **Shopify**: Settings > Online Store > Preferences > Custom code
- **Squarespace**: Settings > Advanced > Code Injection
- **WordPress**: Appearance > Theme Editor > header.php
- **Wix**: Settings > Custom Code > Head
    `
  },
  'melanin-score': {
    title: 'Melanin Score Explained',
    content: `
Melanin Score determines search ranking in "Community Top Picks".

**How It's Calculated**
- Community members can "Boost" any verified business
- Each boost adds MEL to the business's score
- 10% platform fee, 90% added to score

**Benefits of High Score**
- Appears in "Community Top Picks" section
- Better default ranking in search
- Social proof of community support

**Boosting a Business**
1. Visit any verified business page
2. Click "Boost this Business"
3. Enter MEL amount
4. Confirm boost

**Can Anyone Boost?**
Yes! Any logged-in user can boost any verified business. It's a way for the community to support businesses they love.
    `
  },
  'personalized-recommendations': {
    title: 'Personalized Recommendations',
    content: `
Blackified learns your preferences to show you businesses you'll love!

**How Recommendations Work**
Our smart recommendation system analyzes:
- **Your Favorites**: Businesses you've saved
- **Your Reviews**: Categories you review positively
- **Your History**: Recently viewed businesses
- **Your Location**: Businesses near you

**Recommendation Sections**

**For You**
Personalized picks based on your activity. The more you use Blackified, the better these get!

**Discover Something New**
Fresh businesses you haven't seen yet, selected to match your tastes.

**Near You**
Highly-rated businesses within your area. Enable location access for best results.

**Similar Businesses**
When viewing a business page, scroll down to see "Similar Businesses" in the same category nearby.

**Tips for Better Recommendations**
- Save businesses to your favorites
- Write reviews (positive reviews help us understand your preferences)
- Allow location access for nearby suggestions
    `
  },
  'marketplace-selling': {
    title: 'Selling Products & Services',
    content: `
Turn your Blackified business listing into a storefront!

**Requirements**
- Your business must be **claimed** by you
- Your business must be **verified** by admin
- Admin must enable marketplace for your business

**What You Can Sell**

**Physical Products**
- Tangible goods that ship to customers
- Set price, inventory quantity, and shipping info
- Track orders from pending to delivered

**Digital Products**
- Downloads, ebooks, courses, templates
- Instant access after purchase
- No shipping required

**Services**
- Appointments, consultations, sessions
- Set available time slots
- Customers book directly through your page

**How to Start Selling**
1. Go to Dashboard > Products tab (only visible if enabled)
2. Click "Add Product" or "Add Service"
3. Fill in details: name, description, price, images
4. Publish and start selling!

**Payments**
- All purchases use MEL currency
- 10% platform fee on each sale
- Earnings added to your MEL wallet

**Managing Orders**
- View orders in Dashboard > Products tab
- Update status: Pending → Processing → Shipped → Delivered
- MEL is held in escrow until order completed
    `
  },
  'melanin-account': {
    title: 'Melanin Account (Virtual USD)',
    content: `
Hold USD and convert between dollars and MEL seamlessly!

**What is Melanin Account?**
A virtual USD account that lets you:
- Hold US dollars on Blackified
- Convert USD to MEL instantly
- Convert MEL back to USD

**Getting Started**
1. Go to Dashboard > Wallet
2. Look for "Melanin Account" section
3. Click "Create Account"
4. Complete identity verification (KYC)

**KYC Verification**
To comply with regulations, we need to verify your identity:
- Government-issued ID (passport, driver's license, or national ID)
- Photo upload
- Usually approved within 24-48 hours

**Converting Currency**
- **USD → MEL**: 1:1 conversion rate
- **MEL → USD**: 1:1 conversion rate
- Conversions are instant once approved

**Note**: This feature must be enabled by admin for your account.
    `
  },
  'delivery-driver': {
    title: 'Become a Delivery Driver',
    content: `
Earn money delivering orders for local Black-owned businesses!

**Requirements**
- Active Blackified account
- Melanin Account with approved KYC verification
- Valid vehicle (car, motorcycle, bicycle, or walking)

**How to Sign Up**
1. Go to Dashboard > Delivery tab
2. Click "Become a Driver"
3. Complete your Melanin Account KYC if not done
4. Select your vehicle type
5. Start accepting deliveries!

**How It Works**
1. Go online when ready to deliver
2. See available deliveries near you
3. Accept a delivery request
4. Pick up from the business
5. Deliver to customer
6. Get paid!

**Earnings**
- You receive 85% of the delivery fee
- Tips from customers go 100% to you
- Earnings added to your MEL wallet instantly

**Driver Rating**
- Customers rate drivers 1-5 stars
- Maintain high ratings for more delivery opportunities
- Complete deliveries promptly for best ratings

**Note**: Delivery service must be enabled by admin. Contact support if you don't see the Delivery tab.
    `
  },
};

export default function HelpCenterPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(['getting-started']);

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const filteredCategories = categories.map(cat => ({
    ...cat,
    articles: cat.articles.filter(article =>
      article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (articleContent[article.id]?.content || '').toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(cat => cat.articles.length > 0);

  // Helper to parse inline markdown
  const parseInlineMarkdown = (text) => {
    if (!text) return text;
    
    // Split by ** for bold text
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i} className="text-white font-semibold">{part.slice(2, -2)}</strong>;
      }
      // Handle inline code with backticks
      if (part.includes('`')) {
        const codeParts = part.split(/(`[^`]+`)/g);
        return codeParts.map((codePart, j) => {
          if (codePart.startsWith('`') && codePart.endsWith('`')) {
            return <code key={`${i}-${j}`} className="bg-zinc-800 text-green-400 px-1.5 py-0.5 rounded text-sm">{codePart.slice(1, -1)}</code>;
          }
          return codePart;
        });
      }
      return part;
    });
  };

  const renderContent = (content) => {
    const lines = content.split('\n');
    const elements = [];
    let inList = false;
    let listItems = [];
    let listType = 'ul';

    const flushList = () => {
      if (listItems.length > 0) {
        if (listType === 'ol') {
          elements.push(
            <ol key={`list-${elements.length}`} className="list-decimal list-inside space-y-1 my-3 text-zinc-300">
              {listItems}
            </ol>
          );
        } else {
          elements.push(
            <ul key={`list-${elements.length}`} className="list-disc list-inside space-y-1 my-3 text-zinc-300">
              {listItems}
            </ul>
          );
        }
        listItems = [];
        inList = false;
      }
    };

    lines.forEach((line, i) => {
      // Headers (lines that are just **text**)
      if (line.match(/^\*\*[^*]+\*\*$/)) {
        flushList();
        elements.push(
          <h3 key={i} className="text-lg font-semibold text-white mt-6 mb-3">
            {line.replace(/\*\*/g, '')}
          </h3>
        );
        return;
      }

      // Unordered list items
      if (line.match(/^- /)) {
        if (!inList || listType !== 'ul') {
          flushList();
          inList = true;
          listType = 'ul';
        }
        listItems.push(<li key={i}>{parseInlineMarkdown(line.substring(2))}</li>);
        return;
      }

      // Ordered list items
      if (line.match(/^\d+\. /)) {
        if (!inList || listType !== 'ol') {
          flushList();
          inList = true;
          listType = 'ol';
        }
        listItems.push(<li key={i}>{parseInlineMarkdown(line.replace(/^\d+\. /, ''))}</li>);
        return;
      }

      // Table rows - skip for now
      if (line.startsWith('|')) {
        flushList();
        return;
      }

      // Empty lines
      if (line.trim() === '') {
        flushList();
        elements.push(<div key={i} className="h-2" />);
        return;
      }

      // Regular paragraphs
      flushList();
      elements.push(
        <p key={i} className="text-zinc-300 mb-2">
          {parseInlineMarkdown(line)}
        </p>
      );
    });

    flushList();
    return elements;
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      
      <main className="pt-24 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">
                Help Center
              </p>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                How can we help?
              </h1>
              <p className="text-zinc-400 max-w-2xl mx-auto">
                Find answers to common questions about using Blackified, listing your business, and earning MEL rewards.
              </p>
            </motion.div>

            {/* Search */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mt-8 max-w-xl mx-auto"
            >
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <input
                  type="text"
                  placeholder="Search help articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 rounded-xl bg-zinc-900/50 border border-zinc-800 text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-700"
                  data-testid="help-search-input"
                />
              </div>
            </motion.div>
          </div>

          {/* Contact Banner */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass-card rounded-xl p-6 mb-8 flex flex-col md:flex-row items-center justify-between gap-4"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center">
                <HelpCircle className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Need more help?</h3>
                <p className="text-zinc-400 text-sm">Our support team is here for you</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <a 
                href={`tel:${SUPPORT_PHONE.replace(/[^0-9]/g, '')}`}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-zinc-800 text-white hover:bg-zinc-700 transition-colors"
              >
                <Phone className="w-4 h-4" />
                {SUPPORT_PHONE}
              </a>
              <a 
                href={`mailto:${SUPPORT_EMAIL}`}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-black hover:bg-zinc-200 transition-colors"
              >
                <Mail className="w-4 h-4" />
                {SUPPORT_EMAIL}
              </a>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Categories Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-2">
                {filteredCategories.map((category, index) => (
                  <motion.div
                    key={category.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="glass-card rounded-xl overflow-hidden"
                  >
                    <button
                      onClick={() => toggleCategory(category.id)}
                      className="w-full flex items-center justify-between p-4 hover:bg-zinc-800/50 transition-colors"
                      data-testid={`category-${category.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg ${category.bgColor} flex items-center justify-center`}>
                          <category.icon className={`w-5 h-5 ${category.color}`} />
                        </div>
                        <span className="text-white font-medium">{category.title}</span>
                      </div>
                      {expandedCategories.includes(category.id) ? (
                        <ChevronDown className="w-5 h-5 text-zinc-500" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-zinc-500" />
                      )}
                    </button>
                    
                    {expandedCategories.includes(category.id) && (
                      <div className="px-4 pb-4 space-y-1">
                        {category.articles.map(article => (
                          <button
                            key={article.id}
                            onClick={() => setSelectedArticle(article.id)}
                            className={`w-full text-left px-4 py-2 rounded-lg text-sm transition-colors ${
                              selectedArticle === article.id
                                ? 'bg-white text-black'
                                : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
                            }`}
                            data-testid={`article-${article.id}`}
                          >
                            {article.title}
                          </button>
                        ))}
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Article Content */}
            <div className="lg:col-span-2">
              {selectedArticle ? (
                <motion.div
                  key={selectedArticle}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="glass-card rounded-xl p-8"
                >
                  <button
                    onClick={() => setSelectedArticle(null)}
                    className="text-zinc-400 hover:text-white text-sm mb-4 flex items-center gap-1"
                  >
                    ← Back to all articles
                  </button>
                  <h2 className="text-2xl font-bold text-white mb-6">
                    {articleContent[selectedArticle]?.title}
                  </h2>
                  <div className="prose prose-invert max-w-none">
                    {renderContent(articleContent[selectedArticle]?.content || '')}
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="glass-card rounded-xl p-8"
                >
                  <h2 className="text-2xl font-bold text-white mb-6">
                    Welcome to Blackified Help Center
                  </h2>
                  <p className="text-zinc-400 mb-8">
                    Select a topic from the categories on the left, or use the search bar to find specific help articles.
                  </p>

                  {/* Quick Links */}
                  <h3 className="text-lg font-semibold text-white mb-4">Popular Topics</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { id: 'what-is-blackified', icon: Book, title: 'What is Blackified?' },
                      { id: 'list-business', icon: Building2, title: 'Listing Your Business' },
                      { id: 'what-is-mel', icon: Sparkles, title: 'MEL Rewards System' },
                      { id: 'verification-process', icon: Shield, title: 'Verification Process' },
                    ].map(item => (
                      <button
                        key={item.id}
                        onClick={() => setSelectedArticle(item.id)}
                        className="flex items-center gap-3 p-4 rounded-lg bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700 transition-colors text-left"
                      >
                        <item.icon className="w-5 h-5 text-zinc-400" />
                        <span className="text-white">{item.title}</span>
                        <ChevronRight className="w-4 h-4 text-zinc-600 ml-auto" />
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>
          </div>

          {/* Bottom Contact Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-12 text-center"
          >
            <div className="glass-card rounded-xl p-8">
              <h3 className="text-xl font-bold text-white mb-2">Still have questions?</h3>
              <p className="text-zinc-400 mb-6">
                Our support team is available Monday - Friday, 9am - 6pm EST
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a 
                  href={`tel:${SUPPORT_PHONE.replace(/[^0-9]/g, '')}`}
                  className="flex items-center gap-2 px-6 py-3 rounded-lg bg-zinc-800 text-white hover:bg-zinc-700 transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  Call {SUPPORT_PHONE}
                </a>
                <a 
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="flex items-center gap-2 px-6 py-3 rounded-lg bg-white text-black hover:bg-zinc-200 transition-colors"
                >
                  <Mail className="w-5 h-5" />
                  Email {SUPPORT_EMAIL}
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
